import { Input } from "@src/ui/input";
import { MediaFragment } from "@src/fragments.generated";
import { useDebouncedInputValue } from "@src/hooks/useDebouncedInputValue";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import gql from "graphql-tag";
import {
  useAddMediaResourcesTrainingResourcesQuery,
  AddMediaResources_TrainingResourceFragment,
  AddMediaResources_LibraryItemFragment,
} from "./Resources.generated";
import Text from "@src/ui/text";
import { getLinkType, LinkType } from "@src/utils/files";
import { Skeleton } from "@src/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@src/ui/select";
import { compareDesc, parseISO } from "date-fns";
import { ResourceThumbnail } from "@src/components/ui/ResourceThumbnail";
import TrainingResourcePDFRichTextExport from "@src/components/libraryItemDetailPages/resource/pdf/TrainingResourcePDFRichTextExport";
import { extractBodyContent } from "@src/components/libraryItemDetailPages/resource/build/TrainingResourceRichTextEditor";
import {
  TrainingResourceVersionType,
  LibraryItemType,
  LibrarySearchMode,
} from "@src/types.generated";

type FileType = "All" | "PDFs" | "Images" | "Videos" | "Opus Docs";

type Sort = "Date Added" | "Name" | "Type";

type Props = {
  onResourceClick: (
    trainingResource: AddMediaResources_TrainingResourceFragment,
  ) => void;
  filterResources?: LinkType[];
  disableFileTypeSelect?: boolean;
};

const FILE_TYPE_SORT_ORDER: FileType[] = ["PDFs", "Images", "Videos", "All"];

const LINK_TYPE_FILE_TYPE_MAP: Record<LinkType, FileType> = {
  [LinkType.PDF]: "PDFs",
  [LinkType.IMAGE]: "Images",
  [LinkType.VIDEO]: "Videos",
  [LinkType.HOSTED_VIDEO]: "Videos",
  [LinkType.MUX_VIDEO]: "Videos",
  [LinkType.GIF]: "Images",
  [LinkType.AUDIO]: "All",
  [LinkType.LINK]: "All",
  [LinkType.VCARD]: "All",
  [LinkType.EMOJI]: "All",
  [LinkType.WEBSITE]: "All",
};

function getFileType(tr: AddMediaResources_TrainingResourceFragment): FileType {
  if (
    tr.publishedVersion?.type === TrainingResourceVersionType.RichTextDocument
  ) {
    return "Opus Docs";
  }
  const mediaUrl = tr.publishedVersion?.media?.mediaUrls?.en;
  if (!mediaUrl) {
    return "All";
  }

  const linkType = getLinkType(mediaUrl);
  if (linkType === LinkType.PDF) {
    return "PDFs";
  }
  if ([LinkType.GIF, LinkType.IMAGE].includes(linkType)) {
    return "Images";
  }
  if (
    [LinkType.VIDEO, LinkType.HOSTED_VIDEO, LinkType.MUX_VIDEO].includes(
      linkType,
    )
  ) {
    return "Videos";
  }

  return "All";
}

const Loading: FC = () => <Skeleton className="h-[128px] w-[107px] p-2" />;
const PAGE_SIZE = 40;

const Resources: FC<Props> = (props) => {
  const [fileType, setFileType] = useState<FileType>(
    props.filterResources && props.filterResources.length > 0
      ? LINK_TYPE_FILE_TYPE_MAP[props.filterResources[0]]
      : "All",
  );
  const [sort, setSort] = useState<Sort>("Date Added");
  const {
    value: search,
    setValue: setSearch,
    debouncedValue: debouncedSearch,
  } = useDebouncedInputValue({ initialValue: "", delay: 300 });
  const [numVisibleItems, setNumVisibleItems] = useState(PAGE_SIZE);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { data, loading } = useAddMediaResourcesTrainingResourcesQuery({
    variables: {
      input: {
        filters: {
          itemType: { value: LibraryItemType.TrainingResource },
        },
        search: { value: debouncedSearch },
        searchMode: LibrarySearchMode.NameDescriptionAndContent,
      },
    },
  });
  const libraryItems: AddMediaResources_LibraryItemFragment[] = useMemo(
    () => data?.AdminLibrary?.objects || [],
    [data?.AdminLibrary?.objects],
  );

  const totalCount = useMemo(
    () => data?.AdminLibrary?.totalCount || 0,
    [data?.AdminLibrary?.totalCount],
  );

  const filteredLibraryItems: AddMediaResources_LibraryItemFragment[] = useMemo(
    () =>
      libraryItems.filter((r) => {
        const mediaUrl =
          r.trainingResource?.publishedVersion?.media?.mediaUrls?.en;
        if (!r.trainingResource) {
          return false;
        }
        const currentFileType = getFileType(r.trainingResource);
        let filter = true;
        if (props.filterResources && mediaUrl) {
          filter =
            filter && props.filterResources?.includes(getLinkType(mediaUrl));
        }
        if (fileType !== "All") {
          if (fileType === "PDFs") {
            filter = filter && currentFileType === "PDFs";
          }
          if (fileType === "Images") {
            filter = filter && currentFileType === "Images";
          }
          if (fileType === "Videos") {
            filter = filter && currentFileType === "Videos";
          }
          if (fileType === "Opus Docs") {
            filter = filter && currentFileType === "Opus Docs";
          }
        }
        return filter;
      }),
    [props.filterResources, libraryItems, fileType],
  );
  const sortedLibraryItems: AddMediaResources_LibraryItemFragment[] = useMemo(
    () =>
      filteredLibraryItems.sort(
        (
          a: AddMediaResources_LibraryItemFragment,
          b: AddMediaResources_LibraryItemFragment,
        ) => {
          if (!a.trainingResource || !b.trainingResource) {
            return 0;
          }
          if (sort === "Date Added") {
            return compareDesc(parseISO(a.createdAt), parseISO(b.createdAt));
          }
          if (sort === "Name") {
            return a.name.en?.localeCompare(b.name.en || "") || 0;
          }
          if (sort === "Type") {
            const aType = getFileType(a.trainingResource);
            const bType = getFileType(b.trainingResource);
            return (
              FILE_TYPE_SORT_ORDER.indexOf(aType) -
                FILE_TYPE_SORT_ORDER.indexOf(bType) || 0
            );
          }
          return 0;
        },
      ),
    [filteredLibraryItems, sort],
  );
  const visibleLibraryItems = useMemo(() => {
    return sortedLibraryItems.slice(0, numVisibleItems);
  }, [sortedLibraryItems, numVisibleItems]);
  const loadMore = useCallback(() => {
    if (visibleLibraryItems.length >= totalCount) {
      return;
    }
    setNumVisibleItems(numVisibleItems + PAGE_SIZE);
  }, [visibleLibraryItems.length, totalCount, numVisibleItems]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (
        container.scrollTop + container.clientHeight >=
          container.scrollHeight - 200 && // 200px threshold
        !loading
      ) {
        loadMore();
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [loadMore, loading]);
  return (
    <>
      <Input
        placeholder="Search resources..."
        className="mb-2 w-full"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <div className="mb-2 flex gap-1">
        <Select
          defaultValue="All"
          value={fileType}
          disabled={props.disableFileTypeSelect}
          onValueChange={(v) => setFileType(v as FileType)}
        >
          <SelectTrigger className="mr-1">
            <>
              <span className="text-muted-foreground">File type:</span>
              <span className="mx-1">{fileType}</span>
            </>
          </SelectTrigger>
          <SelectContent className="z-[1000]">
            <SelectItem value={"All"}>All</SelectItem>
            <SelectItem value={"Opus Docs"}>Opus Docs</SelectItem>
            <SelectItem value={"PDFs"}>PDFs</SelectItem>
            <SelectItem value={"Images"}>Images</SelectItem>
            <SelectItem value={"Videos"}>Videos</SelectItem>
          </SelectContent>
        </Select>
        <Select
          defaultValue="Date Added"
          value={sort}
          onValueChange={(v) => setSort(v as Sort)}
        >
          <SelectTrigger>
            <>
              <span className="text-muted-foreground">Sort by:</span>
              <span className="mx-1">{sort}</span>
            </>
          </SelectTrigger>
          <SelectContent className="z-[1000]">
            <SelectItem value={"Date Added"}>Date Added</SelectItem>
            <SelectItem value={"Name"}>Name</SelectItem>
            <SelectItem value={"Type"}>Type</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div
        ref={scrollContainerRef}
        className="flex max-h-full w-full flex-wrap justify-start gap-2 overflow-y-auto"
      >
        {loading && libraryItems.length === 0 && (
          <>
            {Array.from({ length: 11 }).map((_, i) => (
              <Loading key={`add-media-loading-${i}`} />
            ))}
          </>
        )}
        {!loading &&
          visibleLibraryItems.length > 0 &&
          visibleLibraryItems.map((item) => {
            if (!item.trainingResource?.publishedVersion) {
              return null;
            }
            return (
              <Resource
                key={`modal-file-list-${item.trainingResource.publishedVersion.id}`}
                onClick={() =>
                  item.trainingResource &&
                  props.onResourceClick(item.trainingResource)
                }
                version={item.trainingResource.publishedVersion}
                name={item.name.en}
              />
            );
          })}
        {!loading && visibleLibraryItems.length === 0 && (
          <Text type="P2" className="text-muted-foreground">
            No resources found
          </Text>
        )}
      </div>
    </>
  );
};
const MediaResource: FC<{
  media: MediaFragment;
  name: string;
  onClick: () => void;
}> = (props) => {
  return (
    <div
      className="flex h-[128px] w-[107px] cursor-pointer flex-col rounded-md bg-muted p-2"
      onClick={props.onClick}
    >
      <div className="max-h-[80px] overflow-hidden">
        <ResourceThumbnail media={props.media} />
      </div>
      <Text type="P3" className="mt-auto truncate">
        {props.name || ""}
      </Text>
    </div>
  );
};
const Resource: FC<{
  version: NonNullable<
    AddMediaResources_TrainingResourceFragment["publishedVersion"]
  >;
  name: string;
  onClick: () => void;
}> = (props) => {
  if (
    props.version.type === TrainingResourceVersionType.Media &&
    props.version.media
  ) {
    return (
      <MediaResource
        media={props.version.media}
        name={props.name}
        onClick={props.onClick}
      />
    );
  }
  if (
    props.version.type === TrainingResourceVersionType.RichTextDocument &&
    props.version.richTextDocument
  ) {
    return (
      <div
        className="flex h-[128px] w-[107px] cursor-pointer flex-col rounded-md bg-muted p-2"
        onClick={props.onClick}
      >
        <div className="max-h-[80px] w-[120px] min-w-[120px] overflow-hidden">
          <div className="origin-top-left scale-[0.1]">
            <TrainingResourcePDFRichTextExport
              title={props.name || ""}
              description={""}
              html={extractBodyContent(
                props.version.richTextDocument.content?.en || "",
              )}
            />
          </div>
        </div>
        <Text type="P3" className="mt-auto truncate">
          {props.name || ""}
        </Text>
      </div>
    );
  }
  return null;
};

export default Resources;

gql`
  fragment AddMediaResources_TrainingResource on TrainingResource {
    id
    publishedVersion {
      id
      type
      media {
        ...Media
      }
      richTextDocument {
        ...RichTextDocument
      }
    }
    libraryItem {
      id
    }
  }
  fragment AddMediaResources_LibraryItem on LibraryItem {
    id
    createdAt
    name {
      ...TranslationSet
    }
    description {
      ...TranslationSet
    }
    trainingResource {
      ...AddMediaResources_TrainingResource
    }
  }
  query AddMediaResourcesTrainingResources(
    $input: AdminLibraryInput!
    $pagination: PaginationInput
  ) {
    AdminLibrary(input: $input, pagination: $pagination) {
      totalCount
      objects {
        ...AddMediaResources_LibraryItem
      }
    }
  }
`;
